import React, { useCallback, useRef, useState } from 'react';
import DOMPurify from 'dompurify';

import { RefinementListProps } from '@white-nl/instantsearch-components/dist/types/components';
import SearchBox from '@white-nl/instantsearch-components/dist/components/searching/SearchBox';
import Check from '../../ui/icons/sharp/solid/Check';

export default function RefinementList({
  refinementList,
  refinementListSettings,
  additionalData,
}: RefinementListProps) {
  const {
    className,
    classNames,
    translations,
    showMore,
    searchable,
    searchablePlaceholder,
  } = refinementListSettings;
  const {
    canToggleShowMore,
    canRefine,
    isShowingMore,
    isFromSearch,
    refine,
    searchForItems,
    toggleShowMore,
  } = refinementList;

  let { items } = refinementList;
  const cmItems = items.filter((item) => item.label.endsWith('cm'))
    .sort(
      (a, b) => {
        const aNum = Number(a.label.replace('cm', ''));
        const bNum = Number(b.label.replace('cm', ''));
        return aNum - bNum;
      },
    );
  const itemItems = items.filter((item) => item.label === 'Item');
  const letterSizeMapping = {
    '3XS': 1,
    XXXS: 1,
    '2XS': 2,
    XXS: 2,
    XS: 3,
    S: 4,
    M: 5,
    L: 6,
    XL: 7,
    '2XL': 8,
    XXL: 8,
    '3XL': 9,
    XXXL: 9,
  };
  const letterSizeItems = items.filter((item) => item.label !== 'Item' && !item.label.endsWith('cm') && Number.isNaN(Number.parseInt(item.label.split('-')[0], 10)))
    .sort((a, b) => {
      const aParts = a.label.split('/');
      const bParts = b.label.split('/');
      const aKey = aParts[0] as keyof typeof letterSizeMapping;
      const bKey = bParts[0] as keyof typeof letterSizeMapping;
      const aScore = letterSizeMapping[aKey] ?? 10;
      const bScore = letterSizeMapping[bKey] ?? 10;
      if (aScore !== bScore) {
        return aScore - bScore;
      }
      return a.label.localeCompare(b.label);
    });
  let otherItems = items.filter((item) => !item.label.endsWith('cm') && Number.isInteger(Number.parseInt(item.label.slice(0, 1), 10)));
  otherItems = otherItems.sort((a, b) => {
    if (a.label.includes('-') && !b.label.includes('-')) {
      return 1;
    }
    if (!a.label.includes('-') && b.label.includes('-')) {
      return -1;
    }
    return a.label.localeCompare(b.label);
  });
  items = [...new Set([...otherItems, ...cmItems, ...letterSizeItems, ...itemItems])];
  const rootClass = [
    'ais-RefinementList',
    classNames?.root,
    !canRefine ? 'ais-RefinementList--noRefinement' : '',
    !canRefine ? 'classNames?.noRefinementRoot' : '',
    className,
  ].filter(Boolean).join(' ');

  const searchBoxClass = [
    'ais-RefinementList-searchBox',
    classNames?.searchBox,
  ].filter(Boolean).join(' ');

  const noResultClass = [
    'ais-RefinementList-noResults',
    classNames?.noResults,
  ].filter(Boolean).join(' ');

  const listClass = [
    'ais-RefinementList-list',
    classNames?.list,
  ].filter(Boolean).join(' ');

  const showMoreClass = [
    'ais-RefinementList-showMore',
    classNames?.showMore,
    !canToggleShowMore ? 'ais-RefinementList-showMore--disabled' : '',
    !canToggleShowMore ? classNames?.disabledShowMore : '',
  ].filter(Boolean).join(' ');

  const noResults = searchable
    && isFromSearch
    && items.length === 0
    && translations?.noResultsText;

  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const setQuery = useCallback((newQuery: string, compositionComplete = true) => {
    setInputValue(newQuery);
    if (compositionComplete) {
      searchForItems(newQuery);
    }
  }, [searchForItems]);

  const onChange = useCallback((event:
                                  React.ChangeEvent<HTMLInputElement> | React.CompositionEvent<HTMLInputElement>) => {
    const compositionComplete = event.type === 'compositionend'
      || !(event.nativeEvent as KeyboardEvent).isComposing;

    setQuery(event.currentTarget.value, compositionComplete);
  }, [setQuery]);

  const onSubmit = useCallback(() => {
    if (items.length > 0) {
      refine(items[0]?.value || '');
      setQuery('');
    }
  }, [items, refine, setQuery]);

  const onReset = useCallback(() => {
    setQuery('');
  }, [setQuery]);

  return (
    <div className={rootClass}>
      {searchable && (
        <div className={searchBoxClass}>
          <SearchBox
            // @ts-ignore
            inputRef={inputRef}
            placeholder={searchablePlaceholder || ''}
            translations={{
              submitButtonTitle: (translations?.submitButtonTitle || ''),
              resetButtonTitle: (translations?.resetButtonTitle || ''),
            }}
            value={inputValue}
            onChange={onChange}
            onSubmit={onSubmit}
            onReset={onReset}
          />
        </div>
      )}
      {noResults ? (
        <div className={noResultClass}>
          {translations?.noResultsText}
        </div>
      ) : (
        <ul className={listClass}>
          {items.map((item) => {
            const itemClass = [
              'ais-RefinementList-item',
              classNames?.item,
              item.isRefined ? 'ais-RefinementList-item--selected' : '',
              item.isRefined ? classNames?.selectedItem : '',
            ].filter(Boolean).join(' ');

            const labelClass = [
              'ais-RefinementList-label',
              classNames?.label,
            ].filter(Boolean).join(' ');

            const inputClass = [
              'ais-RefinementList-checkbox',
              classNames?.checkbox,
            ].filter(Boolean).join(' ');

            const labelTextClass = [
              'ais-RefinementList-labelText',
              classNames?.labelText,
            ].filter(Boolean).join(' ');

            const countClass = [
              'ais-RefinementList-count',
              classNames?.count,
            ].filter(Boolean).join(' ');

            const checkboxMaskClass = [
              'ais-RefinementList-mask',
              labelClass.includes('no-checkbox') ? 'hidden' : '',
              labelClass.includes('color-checkbox') ? 'size-5 rounded-full *:hidden!' : 'flex items-center justify-center absolute -top-px left-0 w-8 h-8 border border-dark/10 rounded-sm text-dark transition-border duration-300 group-has-checked/label:bg-dark group-has-checked/label:border-dark! group-hover/label:border-accent',
            ].filter(Boolean).join(' ');

            const checkboxMaskStyle = labelClass.includes('color-checkbox') && additionalData.colorMap[item.value]
              ? { backgroundColor: additionalData.colorMap[item.value] }
              : {};

            const label = labelClass.includes('sizes') ? item.label.replace(',5', '½') : item.label;

            return (
              <li key={item.value} className={itemClass}>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label className={labelClass}>
                  <input
                    checked={item.isRefined}
                    className={inputClass}
                    type="checkbox"
                    value={item.value}
                    onChange={() => {
                      refine(item.value);
                      setQuery('');
                    }}
                  />
                  <span
                    className={checkboxMaskClass}
                    style={checkboxMaskStyle}
                  >
                    <Check iconClass="w-4 *:fill-light opacity-0 group-has-checked/label:opacity-100" width="16" />
                  </span>
                  <span
                    className={labelTextClass}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(label),
                    }}
                  />
                  <span className={countClass}>
                    {item.count}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
      {showMore && (
        <button
          className={showMoreClass}
          disabled={!canToggleShowMore}
          type="button"
          onClick={toggleShowMore}
        >
          {translations?.showMoreButtonText
            ? translations.showMoreButtonText({ isShowingMore })
            : ''}
        </button>
      )}
    </div>
  );
}
